import { action, makeObservable, observable, runInAction } from 'mobx';
import { Services } from '../../API';
import { Query, QueryOptions } from '../Query';
import axios from 'axios';
import {
  IUserLayerRecord,
  IUserLayerRecordEdit,
  UserLayerCategoryResponse,
} from '@project/API/resources/userLayer';
import { SelectedUserLayer } from './SelectedUserLayer';
import { UserLayer } from './UserLayer';
import { UserLayerLoader } from '../UserLayerLoader';
import { MapProperties } from '@datum-map';
import { notification } from 'antd';
import { Feature } from 'ol';
import { Point } from 'ol/geom';

export type UserLayersFilteredFields = '';
export interface UserLayerCategory extends UserLayerCategoryResponse {
  checked?: boolean;
}

const DEFAULT_QUERY: QueryOptions<UserLayersFilteredFields> = {
  page: 1,
  pageSize: 10,
  search: undefined,
  filter: { orParams: [] },
};

export class UserLayersStore {
  readonly selectedLayer: SelectedUserLayer;
  readonly layerLoader: UserLayerLoader;

  private _services: Services;
  private _userLayerLoaderSignals = new Set<AbortController>();

  @observable filter: Query<UserLayersFilteredFields>;
  @observable private _isLoading = false;
  @observable userLayers: UserLayerCategory[] = [];
  @observable LayersOnMap: UserLayer[] = [];
  @observable private _error?: string = undefined;
  @observable private _totalLayersCount = 0;
  @observable checkedLayers: Set<number> = new Set<number>();
  private _mapProperties: MapProperties;

  constructor(services: Services, _mapProperties: MapProperties) {
    this._services = services;
    this.selectedLayer = new SelectedUserLayer(services);
    this.filter = new Query<UserLayersFilteredFields>(DEFAULT_QUERY);
    this.userLayers = [];
    this.layerLoader = new UserLayerLoader(services, _mapProperties);
    this._mapProperties = _mapProperties;

    makeObservable(this);
  }

  get totalLayersCount() {
    return this._totalLayersCount;
  }

  get error() {
    return this._error;
  }

  get isLoading() {
    return this._isLoading;
  }

  editRecordPlacement = async (
    data: Partial<IUserLayerRecord> & { id: number },
    feature: Feature<Point>,
  ) => {
    const view = this._mapProperties.map.getView();
    const projection = view.getProjection().getCode();

    const record = await this._services.userLayerRecords.getRecord(data.id);

    await this._services.userLayerGeoJSONLayer.patch(
      { geometry: data.geometry, properties: record.properties, id: data.id },
      { projection },
    );

    const layerId = this.layerLoader.getLayerIdByFeature(feature);
    if (layerId) {
      await this.layerLoader.showLayer(
        layerId,
        (title) => notification.error({ title }),
        this.selectedLayer.query.searchParams,
      );
    }
  };

  @action loadUserLayers = async () => {
    if (this._isLoading) {
      this._userLayerLoaderSignals.forEach((s) => s.abort());
      this._userLayerLoaderSignals = new Set();
    }

    const currentSignal = new AbortController();
    this._userLayerLoaderSignals.add(currentSignal);

    runInAction(() => {
      this._isLoading = true;
      this._error = undefined;
    });

    const params = this.filter.searchParams;

    try {
      const result = await this._services.userLayer.get(
        params,
        currentSignal.signal,
      );

      runInAction(() => {
        this.userLayers = result.results;
        this._totalLayersCount = result.count;
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        runInAction(() => {
          this.userLayers = [];
          this._error = 'Произошла ошибка при загрузке слоев';
        });
      }
    } finally {
      this._userLayerLoaderSignals.delete(currentSignal);

      runInAction(() => {
        this._isLoading = this._userLayerLoaderSignals.size > 0;
      });
    }
  };

  @action showLayer = (layerId: number) => {
    this.checkedLayers.add(layerId);
    this.layerLoader.showLayer(layerId, (title) =>
      notification.error({ title }),
    );
  };

  @action hideLayer = (layerId: number) => {
    this.checkedLayers.delete(layerId);
    this.layerLoader.removeLayer(layerId);
  };

  @action editRecord = async (
    recordData: Partial<IUserLayerRecordEdit> & { id: number },
  ) => {
    await this._services.userLayerRecords.patch(recordData);
    await this._services.userLayerPhotos.post(recordData);

    if (
      recordData.properties?.photos &&
      recordData.properties?.photos.removedFiles
    ) {
      await this._services.userLayerPhotos.delete(recordData);
    }
  };

  isLayerChecked = (layerId: number) => {
    return !!this.userLayers.find((item) => item.id === layerId)?.checked;
  };
}
